import React from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { SplitButton } from "@design-system/Button/SplitButton";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    INVOICE_LOCKED_ACTION_TOOLTIP,
    NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP,
} from "@components/invoices/invoiceLockedFieldTooltip";
import { SaveStatus } from "./useNativeInvoiceSave";
import styles from "./NativeInvoiceDrawer.module.scss";

interface Props {
    step: "details" | "send";
    open: boolean;
    saveStatus: SaveStatus;
    // Set while the draft has a field-level validation error the server would
    // reject (see getNativeInvoiceDueDateError): Review & send stays blocked
    // so the user fixes the highlighted field first. Save as draft is never
    // gated on this.
    saveDisabled: boolean;
    // Review & send and Create only require a customer. Create only issues
    // the invoice as OPEN.
    hasCounterparty?: boolean;
    // Review & send and Create only require at least one line whose amount
    // (quantity × rate) is not zero.
    hasNonZeroLineItem: boolean;
    // Both post a journal entry dated on the invoice date, so a date inside a
    // locked accounting period blocks Review & send / Create only (details
    // step) and Send / Schedule send (send step). Save as draft is never
    // gated on this.
    isInvoiceDateLocked: boolean;
    // The backend rejects a negative invoice total on create/update (see
    // NativeInvoiceValidationInternalService#assertInvoiceTotalNotNegative).
    // Kept out of `saveDisabled` / `sendDisabled` upstream and composed here
    // (like the period lock) so the caret trigger stays reachable; unlike the
    // period lock it blocks every action that persists the draft — including
    // Save as draft — since a negative total is never a valid invoice, not just
    // one that can't post a journal entry yet.
    hasNegativeTotal: boolean;
    sendDisabled: boolean;
    // Names the first unmet requirement blocking Send (see
    // getSendDisabledTooltip). Undefined when Send is enabled, or when it's
    // disabled only by a transient pending/saving state that's already
    // self-explanatory via the button's loading spinner.
    sendDisabledTooltip?: string;
    isSending: boolean;
    isDownloading: boolean;
    scheduleEnabled: boolean;
    // Draft-only: Save and Send when FORM_CONTENT is available.
    canSave?: boolean;
    canSend?: boolean;
    canDownload?: boolean;
    onSetStep: (step: "details" | "send") => void;
    onDownload: () => void;
    onSave: () => void;
    // Creates the invoice and leaves: the send step is skipped on purpose.
    onCreateOnly: () => void;
    onSend: () => void;
    onSendTestEmail: () => void;
    // Saves the current draft as a reusable template (prompts for a name).
    // Invoice flow only, so it is optional.
    onSaveAsTemplate?: () => void;
    saveButtonLabel?: string;
    // False until the user first navigates inside the drawer, so the actions
    // don't fade in on the drawer's initial open (only the drawer zoom plays).
    shouldAnimateStepChange?: boolean;
}

function getDisabledActionTooltip({
    hasNonZeroLineItem,
    hasCounterparty,
    isInvoiceDateLocked,
    hasNegativeTotal,
    verb,
}: {
    hasNonZeroLineItem: boolean;
    hasCounterparty: boolean;
    isInvoiceDateLocked: boolean;
    hasNegativeTotal: boolean;
    verb: "create" | "send";
}): string | undefined {
    if (hasNegativeTotal) {
        return NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP;
    }
    if (!hasNonZeroLineItem) {
        return `Add a line item with an amount to ${verb} this invoice`;
    }
    if (!hasCounterparty) {
        return `Add a customer to ${verb} this invoice`;
    }
    if (isInvoiceDateLocked) {
        return INVOICE_LOCKED_ACTION_TOOLTIP;
    }
    return undefined;
}

export const NativeInvoiceCommandBar: React.FC<Props> = ({
    step,
    open,
    saveStatus,
    saveDisabled,
    hasCounterparty = false,
    hasNonZeroLineItem,
    isInvoiceDateLocked,
    hasNegativeTotal,
    sendDisabled,
    sendDisabledTooltip,
    isSending,
    isDownloading,
    scheduleEnabled,
    canSave = true,
    canSend = true,
    canDownload = true,
    onSetStep,
    onDownload,
    onSave,
    onCreateOnly,
    onSend,
    onSendTestEmail,
    onSaveAsTemplate,
    saveButtonLabel = "Create",
    shouldAnimateStepChange = false,
}) => {
    const isCreateOnlyDisabled =
        !hasCounterparty ||
        !hasNonZeroLineItem ||
        isInvoiceDateLocked ||
        hasNegativeTotal;
    const isReviewAndSendDisabled = saveDisabled || isCreateOnlyDisabled;
    // Both the period lock and a negative total are kept out of `saveDisabled`
    // upstream (see useNativeInvoiceHandlers). The caret menu stays reachable,
    // so each menu item that would post ("Create only") or persist the draft
    // ("Save as template") is individually disabled + tooltipped.
    // The send step's primary action posts a journal entry, so fold the period
    // lock and a negative total into its disabled state on top of the generic
    // send preconditions. Both are kept out of `sendDisabled` upstream (see
    // useNativeInvoiceHandlers) so the send-step caret ("Send test email" /
    // "Save as template") stays reachable when either is the only blocker.
    const isSendActionDisabled =
        sendDisabled || isInvoiceDateLocked || hasNegativeTotal;
    let sendActionTooltip: string | undefined;
    if (hasNegativeTotal) {
        sendActionTooltip = NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP;
    } else if (isInvoiceDateLocked) {
        sendActionTooltip = INVOICE_LOCKED_ACTION_TOOLTIP;
    } else {
        sendActionTooltip = sendDisabledTooltip;
    }

    return (
        <CommandIsland className={styles.commandBar}>
            <div className={styles.commandBarGroup}>
                {canDownload && (
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        onClick={onDownload}
                        loading={isDownloading}
                        disabled={
                            isDownloading || saveDisabled || hasNegativeTotal
                        }
                    >
                        Download PDF
                    </ButtonWithLoader>
                )}
            </div>
            <div className={styles.commandBarGroup}>
                {step === "details" ? (
                    <div
                        key="details"
                        className={classNames(
                            styles.commandBarGroup,
                            shouldAnimateStepChange && styles.stepFade,
                        )}
                    >
                        {canSave && canSend && (
                            <Tooltip
                                content={
                                    hasNegativeTotal
                                        ? NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                        : undefined
                                }
                            >
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={onSave}
                                    disabled={hasNegativeTotal}
                                >
                                    Save as draft
                                </Button>
                            </Tooltip>
                        )}
                        {canSave && !canSend && (
                            <Tooltip
                                content={
                                    hasNegativeTotal
                                        ? NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                        : undefined
                                }
                            >
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    onClick={onSave}
                                    loading={saveStatus === "saving"}
                                    disabled={saveDisabled || hasNegativeTotal}
                                    hotKey="mod+enter"
                                    hotKeyDisplayPosition="inline"
                                    hotKeyOptions={{
                                        description: saveButtonLabel,
                                        enabled:
                                            !!open &&
                                            !saveDisabled &&
                                            !hasNegativeTotal,
                                        // The whole step is a form: without this the
                                        // shortcut dies while the caret sits in any
                                        // field, which is where it is used from.
                                        enableOnFormTags: true,
                                        preventDefault: true,
                                    }}
                                >
                                    {saveButtonLabel}
                                </ButtonWithLoader>
                            </Tooltip>
                        )}
                        {/* Split primary action: the default advances to the send
                        step; "Create only" just creates without going there.
                        mod+enter follows the default. */}
                        {canSend && (
                            <WithHotkey
                                tooltip={getDisabledActionTooltip({
                                    hasNonZeroLineItem,
                                    hasCounterparty,
                                    isInvoiceDateLocked,
                                    hasNegativeTotal,
                                    verb: "send",
                                })}
                                hotKey="mod+enter"
                                hotKeyDisplayPosition="inline"
                                hotKeyOptions={{
                                    description: "Review & send email",
                                    enabled: !!open && !isReviewAndSendDisabled,
                                    enableOnFormTags: true,
                                    preventDefault: true,
                                }}
                            >
                                {(hotkeyHint) => (
                                    <SplitButton
                                        variant="primary"
                                        size="md"
                                        onPrimaryClick={() => onSetStep("send")}
                                        disabled={isReviewAndSendDisabled}
                                        menuAriaLabel="More create options"
                                        menu={
                                            <>
                                                <Menu.Item
                                                    disabled={
                                                        isCreateOnlyDisabled ||
                                                        saveStatus === "saving"
                                                    }
                                                    disabledReason={getDisabledActionTooltip(
                                                        {
                                                            hasNonZeroLineItem,
                                                            hasCounterparty,
                                                            isInvoiceDateLocked,
                                                            hasNegativeTotal,
                                                            verb: "create",
                                                        },
                                                    )}
                                                    onClick={onCreateOnly}
                                                >
                                                    Create only
                                                </Menu.Item>
                                                {onSaveAsTemplate && (
                                                    <Menu.Item
                                                        disabled={
                                                            hasNegativeTotal
                                                        }
                                                        disabledReason={
                                                            NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                                        }
                                                        onClick={
                                                            onSaveAsTemplate
                                                        }
                                                    >
                                                        Save as template
                                                    </Menu.Item>
                                                )}
                                            </>
                                        }
                                    >
                                        Review & send email
                                        {hotkeyHint}
                                    </SplitButton>
                                )}
                            </WithHotkey>
                        )}
                    </div>
                ) : (
                    <div
                        key="send"
                        className={classNames(
                            styles.commandBarGroup,
                            shouldAnimateStepChange && styles.stepFade,
                        )}
                    >
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => onSetStep("details")}
                        >
                            Back
                        </Button>
                        <WithHotkey
                            tooltip={sendActionTooltip}
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{
                                description: scheduleEnabled
                                    ? "Schedule send"
                                    : "Send",
                                enabled: !!open && !isSendActionDisabled,
                                enableOnFormTags: true,
                                preventDefault: true,
                            }}
                        >
                            {(hotkeyHint) => (
                                <SplitButton
                                    variant="primary"
                                    size="md"
                                    onPrimaryClick={onSend}
                                    disabled={isSendActionDisabled}
                                    loading={isSending}
                                    menuAriaLabel="More send options"
                                    menu={
                                        <>
                                            <Menu.Item
                                                disabled={hasNegativeTotal}
                                                disabledReason={
                                                    NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                                }
                                                onClick={onSendTestEmail}
                                            >
                                                Send test email
                                            </Menu.Item>
                                            {onSaveAsTemplate && (
                                                <Menu.Item
                                                    disabled={hasNegativeTotal}
                                                    disabledReason={
                                                        NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                                    }
                                                    onClick={onSaveAsTemplate}
                                                >
                                                    Save as template
                                                </Menu.Item>
                                            )}
                                        </>
                                    }
                                >
                                    {scheduleEnabled ? "Schedule send" : "Send"}
                                    {hotkeyHint}
                                </SplitButton>
                            )}
                        </WithHotkey>
                    </div>
                )}
            </div>
        </CommandIsland>
    );
};
